import { Component, EventEmitter, Input, Output } from '@angular/core';
import { SubtaskModel } from '../../service/task-mgmt.service';

@Component({
  selector: 'app-subtask-compact-card',
  templateUrl: './subtask-compact-card.component.html',
  styleUrls: ['./subtask-compact-card.component.scss'],
})
export class SubtaskCompactCardComponent {
  @Input() subtask!: SubtaskModel;
  /** Resolved task type lookup options, plumbed from `task-management-wf` →
   *  drilldown panel → mini-kanban → here. Used by `getTaskTypeName`. */
  @Input() taskTypeOptions: any[] = [];
  @Output() cardClicked = new EventEmitter<SubtaskModel>();
  @Output() commentsClicked = new EventEmitter<SubtaskModel>();
  @Output() attachmentsClicked = new EventEmitter<SubtaskModel>();

  get priorityBorderColor(): string {
    const raw = this.subtask as any;
    const name = (raw?._raw?.task_priority_name || '').toLowerCase();
    if (name.includes('high')) return '#ef4444';
    if (name.includes('med')) return '#f97316';
    if (name.includes('low')) return '#22c55e';
    return '#d1d5db';
  }

  onCardClick(event: Event): void {
    event.stopPropagation();
    if (this.subtask) this.cardClicked.emit(this.subtask);
  }

  /** Format an ISO date as "DD Mmm YYYY" for the "Due" line. */
  formatDueDate(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }

  get isOverdue(): boolean {
    const endDate = this.subtask?.endDate;
    if (!endDate) return false;
    // Status name lives on `_raw.task_status_name` after the new mapper runs.
    const statusName = ((this.subtask as any)?._raw?.task_status_name || '').toLowerCase().trim();
    if (statusName === 'done' || statusName === 'closed' || statusName === 'complete'
      || statusName === 'completed' || statusName === 'cancelled' || statusName === 'canceled') {
      return false;
    }
    const end = new Date(endDate);
    if (isNaN(end.getTime())) return false;
    return end.getTime() < Date.now();
  }

  /** Return only the tag entries that are full objects with `category_name`.
   *  SubtaskModel.tags is currently number[] for in-memory store entries; this
   *  will populate once the API mapper preserves the rich tag shape.
   *  TODO: Resolve tag IDs to names once tag lookup is wired through TaskMgmtService. */
  get entityTags(): any[] {
    const raw: any = this.subtask?.tags;
    if (!Array.isArray(raw)) return [];
    return raw.filter((t: any) => t && typeof t === 'object' && t.category_name);
  }

  /** Resolve a subtask's task type display name via the plumbed `taskTypeOptions`. */
  getTaskTypeName(): string {
    const s: any = this.subtask;
    if (!s) return '';
    // Prefer the raw API name (preserved by mapStoreSubtaskToModel) so the
    // chip renders even when taskTypeOptions hasn't been plumbed in.
    const rawName = s._raw?.task_type_name;
    if (rawName) return String(rawName).trim();
    const raw = s.taskType ?? s.task_type_id;
    if (raw == null || raw === '') return '';
    if (typeof raw === 'string' && isNaN(Number(raw))) return raw;
    const numericId = Number(raw);
    const match = (this.taskTypeOptions || []).find(
      (t: any) => t.value === numericId || t.value === raw
    );
    return match ? match.label : String(raw);
  }

  /** Display name for the subtask's priority. Reads `_raw.task_priority_name`
   *  first (preserved by mapStoreSubtaskToModel in task-management-wf), then
   *  falls back to empty string. No `priorityOptions` lookup needed because the
   *  API response always carries the name (verified by the existing
   *  `_raw.task_status_name` consumer pattern at line 43). */
  getPriorityName(): string {
    const s: any = this.subtask;
    if (!s) return '';
    const rawName = s._raw?.task_priority_name;
    if (rawName) return String(rawName).trim();
    return '';
  }

  /** Map the resolved priority name to the chip CSS class. */
  getPriorityClass(): string {
    const name = this.getPriorityName().toLowerCase();
    if (name === 'high') return 'st-priority-high';
    if (name === 'medium') return 'st-priority-medium';
    if (name === 'low') return 'st-priority-low';
    return 'st-priority-default';
  }

  /** Return the assignee email list for the subtask, tolerating multiple shapes. */
  getAssignees(): string[] {
    const s: any = this.subtask;
    if (!s) return [];
    if (Array.isArray(s.assignees) && s.assignees.length) {
      return s.assignees
        .map((a: any) => (typeof a === 'string' ? a : (a?.assignee_email || a?.email)))
        .filter((e: any) => !!e);
    }
    if (Array.isArray(s.assignee_emails) && s.assignee_emails.length) {
      return s.assignee_emails.filter((e: any) => !!e);
    }
    if (s.assignTo) return [s.assignTo];
    return [];
  }

  /** Initials from an email address — `john.doe@example.com` → `JD`. */
  getInitials(email: string | null | undefined): string {
    if (!email) return '?';
    const prefix = email.split('@')[0] || email;
    const parts = prefix.split(/[._\-+]/).filter((p) => p.length > 0);
    const first = (parts[0]?.[0] || '').toUpperCase();
    const second = (parts[1]?.[0] || parts[0]?.[1] || '').toUpperCase();
    return (first + second) || '?';
  }

  // --- Comment / Attachment badge helpers ---

  getCommentCount(): number {
    const s: any = this.subtask;
    return s?._commentCount ?? s?._raw?.comment_count ?? 0;
  }

  getAttachmentCount(): number {
    const s: any = this.subtask;
    const direct = s?._attachmentCount ?? s?._raw?.attachment_count;
    if (typeof direct === 'number' && direct > 0) return direct;
    return Array.isArray(s?._raw?.attachments) ? s._raw.attachments.length : 0;
  }

  onCommentsClick(event: Event): void {
    event.stopPropagation();
    if (this.subtask) this.commentsClicked.emit(this.subtask);
  }

  onAttachmentsClick(event: Event): void {
    event.stopPropagation();
    if (this.subtask) this.attachmentsClicked.emit(this.subtask);
  }
}
